import { makeAutoObservable } from 'mobx'

import { setSettingForApp } from 'entities/user/module/app-settings'
import userStore from 'entities/user/module/user-store'

import type { RealtyObject } from 'shared/api/kyrgyzstan/search'

const KYRGYZSTAN_DETAIL_SELECTION_STORAGE_KEY =
  'kyrgyzstan_new_detail_selection'

interface KyrgyzstanDetailSelection {
  selectedParent: RealtyObject | null
  selectedChild: RealtyObject | null
}

class KyrgyzstanDetailSelectionStore {
  selectedParent: RealtyObject | null = null

  selectedChild: RealtyObject | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get effectiveRealty() {
    return this.selectedChild ?? this.selectedParent
  }

  selectParent = (parent: RealtyObject) => {
    void setSettingForApp({
      key: KYRGYZSTAN_DETAIL_SELECTION_STORAGE_KEY,
      value: '',
    })

    this.selectedParent = parent
    this.selectedChild = null
  }

  selectChild = (child: RealtyObject) => {
    void setSettingForApp({
      key: KYRGYZSTAN_DETAIL_SELECTION_STORAGE_KEY,
      value: '',
    })

    this.selectedChild = child
  }

  clearChild = () => {
    void setSettingForApp({
      key: KYRGYZSTAN_DETAIL_SELECTION_STORAGE_KEY,
      value: '',
    })

    this.selectedChild = null
  }

  saveSelection = () => {
    if (!this.selectedParent) {
      this.clearSelection()

      return
    }

    const selection: KyrgyzstanDetailSelection = {
      selectedParent: this.selectedParent,
      selectedChild: this.selectedChild,
    }

    void setSettingForApp({
      key: KYRGYZSTAN_DETAIL_SELECTION_STORAGE_KEY,
      value: JSON.stringify(selection),
    })
  }

  restoreSelection = () => {
    if (this.effectiveRealty) return

    const savedSelection = userStore.getClientStateParse<
      Partial<KyrgyzstanDetailSelection>
    >(KYRGYZSTAN_DETAIL_SELECTION_STORAGE_KEY)

    if (!savedSelection) return

    if (!savedSelection.selectedParent) {
      this.clearSelection()

      return
    }

    const hasInvalidChild =
      savedSelection.selectedChild &&
      savedSelection.selectedChild.parent_id !==
        savedSelection.selectedParent.id

    if (hasInvalidChild) {
      this.clearSelection()

      return
    }

    this.selectedParent = savedSelection.selectedParent ?? null
    this.selectedChild = savedSelection.selectedChild ?? null
    void setSettingForApp({
      key: KYRGYZSTAN_DETAIL_SELECTION_STORAGE_KEY,
      value: '',
    })
  }

  clearSelection = () => {
    this.selectedParent = null
    this.selectedChild = null
    void setSettingForApp({
      key: KYRGYZSTAN_DETAIL_SELECTION_STORAGE_KEY,
      value: '',
    })
  }

  reset = () => {
    this.clearSelection()
  }
}

export const kyrgyzstanDetailSelectionStore =
  new KyrgyzstanDetailSelectionStore()
